// Service Worker を管理するReact Hook

import { useState, useEffect, useRef } from 'react';
import { serviceWorkerManager, performanceMonitor, type ServiceWorkerConfig } from '../services/serviceWorker';

interface ServiceWorkerState {
  isRegistered: boolean;
  isOnline: boolean;
  hasUpdate: boolean;
  isInstalling: boolean;
  version: string | null;
  registration: ServiceWorkerRegistration | null;
}

interface ServiceWorkerActions {
  skipWaiting: () => void;
  clearCache: () => Promise<boolean>;
  checkForUpdates: () => Promise<void>;
  unregister: () => Promise<boolean>;
}

interface UseServiceWorkerReturn extends ServiceWorkerState, ServiceWorkerActions {}

export function useServiceWorker(config: ServiceWorkerConfig = {}): UseServiceWorkerReturn {
  const mountedRef = useRef(true);
  const [state, setState] = useState<ServiceWorkerState>({
    isRegistered: false,
    isOnline: navigator.onLine,
    hasUpdate: false,
    isInstalling: false,
    version: null,
    registration: null
  });

  useEffect(() => {
    let mounted = true;

    // Service Worker の設定
    const swConfig: ServiceWorkerConfig = {
      ...config,
      onUpdate: (registration) => {
        if (mounted) {
          setState(prev => ({ ...prev, hasUpdate: true, registration }));
        }
        config.onUpdate?.(registration);
      },
      onSuccess: (registration) => {
        if (mounted) {
          setState(prev => ({ 
            ...prev, 
            isRegistered: true, 
            isInstalling: false,
            registration 
          }));
        }
        config.onSuccess?.(registration);
      },
      onOffline: () => {
        if (mounted) {
          setState(prev => ({ ...prev, isOnline: false }));
        }
        config.onOffline?.();
      },
      onOnline: () => {
        if (mounted) {
          setState(prev => ({ ...prev, isOnline: true }));
        }
        config.onOnline?.();
      }
    };

    // Service Worker を登録
    const registerServiceWorker = async () => {
      if (import.meta.env.DEV) {
        console.log('Service Worker: 開発環境のため登録をスキップ');
        return;
      }

      setState(prev => ({ ...prev, isInstalling: true }));
      
      const success = await serviceWorkerManager.register('/sw.js');
      
      if (mounted) {
        if (success) {
          const version = await serviceWorkerManager.getVersion();
          const registration = serviceWorkerManager.getRegistration();
          
          setState(prev => ({
            ...prev,
            isRegistered: success,
            isInstalling: false,
            version,
            registration
          }));
        } else {
          setState(prev => ({ ...prev, isInstalling: false }));
        }
      }
    };

    // Service Worker 設定を適用
    Object.assign(serviceWorkerManager['config'], swConfig);
    
    // 登録を実行
    registerServiceWorker();

    // パフォーマンス監視を開始
    performanceMonitor.measurePageLoad();
    performanceMonitor.measureResourceLoad();
    performanceMonitor.measureWebVitals();

    return () => {
      mounted = false;
    };
  }, []); // 依存配列を空にして、初回のみ実行

  // アクション関数
  const skipWaiting = () => {
    serviceWorkerManager.skipWaiting();
    setState(prev => ({ ...prev, hasUpdate: false }));
  };

  const clearCache = async (): Promise<boolean> => {
    const success = await serviceWorkerManager.clearCache();
    if (success) {
      // キャッシュクリア後にページをリロード
      window.location.reload();
    }
    return success;
  };

  const checkForUpdates = async (): Promise<void> => {
    const registration = serviceWorkerManager.getRegistration();
    if (registration) {
      await registration.update();
    }
  };

  const unregister = async (): Promise<boolean> => {
    const success = await serviceWorkerManager.unregister();
    if (success && mountedRef.current) {
      setState(prev => ({
        ...prev,
        isRegistered: false,
        hasUpdate: false,
        version: null,
        registration: null
      }));
    }
    return success;
  };

  return {
    // State
    isRegistered: state.isRegistered,
    isOnline: state.isOnline,
    hasUpdate: state.hasUpdate,
    isInstalling: state.isInstalling,
    version: state.version,
    registration: state.registration,
    
    // Actions
    skipWaiting,
    clearCache,
    checkForUpdates,
    unregister
  };
}

// オフライン状態管理のためのHook
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setWasOffline(false);
        // オフラインから復帰時の処理
        console.log('ネットワークが復帰しました');
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      console.log('ネットワークが切断されました');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return {
    isOnline,
    wasOffline,
    isOffline: !isOnline
  };
}

// パフォーマンス監視のためのHook
export function usePerformanceMonitor() {
  const [metrics, setMetrics] = useState<{
    lcp?: number;
    fid?: number;
    cls?: number;
    loadTime?: number;
  }>({});

  useEffect(() => {
    // パフォーマンスメトリクスの収集
    const collectMetrics = () => {
      if (!window.performance) return;

      // ページロード時間
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        const loadTime = navigation.loadEventEnd - navigation.startTime;
        setMetrics(prev => ({ ...prev, loadTime }));
      }
    };

    // ページロード完了後にメトリクス収集
    if (document.readyState === 'complete') {
      collectMetrics();
    } else {
      window.addEventListener('load', collectMetrics);
      return () => window.removeEventListener('load', collectMetrics);
    }
  }, []);

  return metrics;
}

// キャッシュ制御のためのHook
export function useCacheControl() {
  const [cacheSize, setCacheSize] = useState<number | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  // キャッシュサイズを取得
  const getCacheSize = async () => {
    if (!('storage' in navigator)) return;

    try {
      const estimate = await navigator.storage.estimate();
      setCacheSize(estimate.usage || 0);
    } catch (error) {
      console.warn('キャッシュサイズ取得失敗:', error);
    }
  };

  // キャッシュをクリア
  const clearAllCaches = async () => {
    setIsClearing(true);

    try {
      // Service Worker キャッシュをクリア
      await serviceWorkerManager.clearCache();
      
      // ブラウザキャッシュもクリア可能であればクリア
      if ('storage' in navigator && 'clear' in navigator.storage) {
        await (navigator.storage as any).clear();
      }
      
      setCacheSize(0);
      
    } catch (error) {
      console.error('キャッシュクリア失敗:', error);
    } finally {
      setIsClearing(false);
    }
  };

  useEffect(() => {
    getCacheSize();
  }, []);

  return {
    cacheSize,
    isClearing,
    getCacheSize,
    clearAllCaches
  };
}